import React, { useContext, useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getAdminById, updateAdmin, getAllStates, getAllCities, getAllPincode } from '../../middleware/backend_hepler';
import { AuthContext } from '../../Context/AuthContext';
import { validationRules, validateDOB } from '../../utils/validationRules';
import { Button, Row, Col } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../components/Loader';
import LabeledSelect from '../../components/LabeledSelect';
import LabeledDropDown from '../../components/LabeledDropDown';
import LabeledInput from '../../components/LabeledInput';
import LabeledSelectAgent from '../../components/LabeledSelectAgent';
import employeeProfile from "../../assets/default-profile.png"


const Profile = () => {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const { adminId, adminRole } = useContext(AuthContext);
  const [adminData, setAdminData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [pincodenumber, setPincode] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [filteredStates, setFilteredStates] = useState([]);

  const methods = useForm({
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      address: '',
      city: '',
      state: '',
      role: '',
      status: '',
      pincode: '',
      dob: '',
      age: '',
      photo: ''
    },
    mode: 'onChange',
    reValidateMode: "onChange",
  });

  const { handleSubmit, setValue, watch, formState: { errors } } = methods;
  const dob = watch('dob');

  useEffect(() => {
    const fetchAdminData = async () => {
      setLoading(true);
      if (adminId) {
        try {
          const data = await getAdminById(adminId);
          Object.keys(data).forEach((key) => {
            setValue(key, data[key]);
          });
          setAdminData(data);
          setSelectedState(data.state);
        } catch (error) {
          console.error("Failed to fetch admin data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAdminData();
  }, [adminId, setValue, methods]);

  useEffect(() => {
    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();

      if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age >= 0) {
        setValue("age", age);
      } else {
        setValue("age", "");
      }
    }
  }, [dob, setValue]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesData = await getAllStates();
        const availableStates = statesData.filter(
          (state) => state.availability === "Yes"
        );
        setFilteredStates(availableStates);
      } catch (error) {
        console.error("Error fetching states data:", error);
      }
    };
    fetchStates();
  }, []);

  const stateOptions = filteredStates.map((state) => ({
    value: state.name,
    label: state.name,
  }));

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesData = await getAllCities();
        citiesData.sort((a, b) => a.name.localeCompare(b.name));
        setCities(citiesData);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchPinCode = async () => {
      try {
        const pincodeData = await getAllPincode();
        setPincode(pincodeData);
      } catch (error) {
        console.error("Error fetching pincodes:", error);
      }
    };
    fetchPinCode();
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setCityOptions([]);
      setPincodeOptions([]);
    } else {
      const filteredCities = cities.filter(
        (city) => city.State_name === selectedState
      );

      const cityOptions = filteredCities.map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setCityOptions(cityOptions);

      const filteredPincodes = pincodenumber.filter(
        (pincode) => pincode.state === selectedState.toUpperCase()
      );

      const uniquePincodes = new Set();
      const uniqueOptions = [];
      filteredPincodes.forEach((pincode) => {
        if (!uniquePincodes.has(pincode.pincode)) {
          uniquePincodes.add(pincode.pincode);
          uniqueOptions.push({
            value: pincode.pincode,
            label: pincode.pincode,
          });
        }
      });

      uniqueOptions.sort((a, b) => a.value - b.value);
      setPincodeOptions(uniqueOptions);
    }
  }, [selectedState, cities, pincodenumber]);

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption?.value || null);
    setValue("state", selectedOption?.value || "");
  };

  const getSelectedStateValue = () => {
    return stateOptions.find((option) => option.value === selectedState);
  };

  const onSubmit = async (data) => {
    const formData = new FormData();

    for (const key in data) {
      formData.append(key, data[key]);
    }

    if (selectedFile) {
      formData.append("photo", selectedFile);
    }

    try {
      await updateAdmin(adminId, formData);
      toast.success("Profile updated successfully!");
      window.scrollTo(0, 0);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error.includes("Duplicate entry") &&
        error.response.data.error.includes("for key 'ADMIN.mobile'")
      ) {
        toast.error(
          "Mobile number already exists. Please use a different mobile number."
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error.includes("Duplicate entry")
      ) {
        toast.error("Email already exists. Please use a different email.");
      } else {
        console.error("Error updating profile:", error);
        toast.error("Failed to update the profile. Please try again.");
      }
    }
  };

  const handleViewClick = () => {
    if (adminData && adminData.photo) {
      window.open(decodeURIComponent(adminData.photo), '_blank');
    } else {
      toast.warning('No image available.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setAdminData({ ...adminData, photo: URL.createObjectURL(file) });
  };

  const getInitials = (name) => {
    if (!name) return '';
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials;
  };

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <div>{error}</div>;
  }

  const handleGoBack = () => {
    if (isLoggedIn) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="container">
      <div className="edit-back-icon">
        <div className="edit-back-text" onClick={handleGoBack}>
          &lt; Go Back
        </div>
      </div>
      <h2 className="my-4">Profile</h2>
      {adminData && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="avatar-container mb-4 text-center">
              {adminData.photo ? (
                <img
                  src={
                    adminData.photo
                      ? decodeURIComponent(adminData.photo)
                      : "default-avatar.png"
                  }
                  onClick={handleViewClick}
                  className="avatar-image"
                />
              ) : adminData?.name ? (
                <div className="agent-avatar-initials" onClick={handleViewClick}>
                  {getInitials(adminData.name)}
                </div>
              ) : (
                 <img
                  src={employeeProfile}
                  className="avatar-image"
                  alt="Default Profile"
                />
              )}
            </div>
            <div className="upload-container mb-4 text-center">
              <input
                type="file"
                id="photo"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                variant="primary"
                onClick={() => document.getElementById("photo").click()}
                className="profile-image"
              >
                Edit Profile image
              </Button>
            </div>
            <div className="sections personal-details-sections">
              <h4>Personal Details</h4>
              <Row>
                <Col md={6}>
                  <LabeledInput
                    name="name"
                    label="Name"
                    registerOptions={validationRules.name}
                    required
                  />
                </Col>
                <Col md={6}>
                  <LabeledInput
                    name="dob"
                    label="Date of Birth"
                    type="date"
                    registerOptions={{
                      required: "Date of Birth is required",
                      validate: validateDOB,
                    }}
                    required
                  />
                </Col>
                <Col md={6}>
                  <LabeledInput name="age" label="Age" readOnly />
                </Col>
                <Col md={6}>
                  <LabeledInput
                    name="email"
                    label="Email ID"
                    registerOptions={validationRules.email}
                    required
                  />
                </Col>
                <Col md={6}>
                  <LabeledInput
                    name="mobile"
                    label="Mobile"
                    type="tel"
                    registerOptions={validationRules.mobile}
                    required
                  />
                </Col>
                <Col md={6}>
                  <LabeledInput
                    name="address"
                    label="Address"
                    registerOptions={{ required: "Address is required" }}
                    required
                  />
                </Col>
                {adminRole === "admin" && (
                  <Col md={6}>
                    <LabeledDropDown
                      label="User Role"
                      name="role"
                      options={[
                        { value: "admin", label: "admin" },
                        { value: "employee", label: "employee" },
                        { value: "customer", label: "customer" },
                      ]}
                      registerOptions={{ required: "Selection is required" }}
                      required
                    />
                  </Col>
                )}

                {adminRole === "admin" && (
                  <Col md={6}>
                    <LabeledDropDown
                      name="status"
                      label="Status"
                      options={[
                        { label: "Active", value: "Active" },
                        { label: "Inactive", value: "Inactive" },
                      ]}
                      registerOptions={{ required: "Status is required" }}
                    />
                  </Col>
                )}
                <Col md={6}>
                  <LabeledSelect
                    name="state"
                    label="State"
                    options={stateOptions}
                    value={getSelectedStateValue()}
                    onChange={handleStateChange}
                    registerOptions={{ required: "State is required" }}
                    error={errors.state?.message}
                  />
                </Col>
                <Col md={6}>
                  <LabeledSelect
                    name="city"
                    label="City"
                    options={cityOptions}
                    registerOptions={{ required: "City is required" }}
                    error={errors.city?.message}
                  />
                </Col>
                <Col md={6}>
                  <LabeledSelectAgent
                    name="pincode"
                    label="Pincode"
                    options={pincodeOptions}
                    registerOptions={{ required: "Pincode is required" }}
                  />
                </Col>
              </Row>
            </div>
            <div className="button-container mt-4 text-center">
              <Button
                variant="success"
                type="submit"
                className="custom-submit-button"
              >
                Submit
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
      <ToastContainer />
    </div>
  );
};

export default Profile;